import { Link } from "react-router-dom";
import cn from "classnames";
import s from "../sidebar.module.sass";
import { CollapsibleGroup } from "./CollapsibleGroup";
import { SECTION_ICON } from "./sidebar.icons";

// `borrowerSafe` marks the curated read-only subset a borrower may see of their
// own book — portfolio overview, what's in it, what they originated/collected.
// Internal lenses (credit-quality scoring, underwriting, P&L, activity log) and
// admin-only surfaces (data room, config) stay hidden from the borrower.
const BOOK_TABS: { id: string; label: string; adminOnly?: boolean; borrowerSafe?: boolean }[] = [
  // The originator's own presentation card — declared content, no internal
  // lens on it, so the borrower reads it like anybody else.
  { id: "profile", label: "Profile", borrowerSafe: true },
  { id: "snapshot", label: "Snapshot", borrowerSafe: true },
  { id: "quality", label: "Credit Quality", borrowerSafe: true },
  { id: "composition", label: "Composition", borrowerSafe: true },
  { id: "origination", label: "Origination", borrowerSafe: true },
  { id: "underwriting", label: "Underwriting", borrowerSafe: true },
  { id: "pnl", label: "P&L", borrowerSafe: true },
  { id: "collections", label: "Collections", borrowerSafe: true },
  // The DRAFT facility's stress over the book: Debita's proposed structure and
  // what it withstands. Visible to whoever may open the book, since access is
  // settled by team permission on the book itself; which facilities a reader may
  // see is settled per facility, in the facilities section.
  { id: "proposal", label: "Stress & Coverage", borrowerSafe: true },
  // The originator's own accounts: the borrower reads its figures; the original
  // documents stay admin-only inside the tab. Placed with the documentary tabs at
  // the end, after the tabs that read the book's portfolio.
  { id: "financials", label: "Financials", borrowerSafe: true },
  { id: "uploads", label: "Data Room", adminOnly: true },
  { id: "activity", label: "Activity", adminOnly: true },
  { id: "mapping", label: "Mapping", adminOnly: true },
];

export function BookGroup({ bookId, currentBookId, activeTab, isAdmin, defaultOpen }: {
  bookId: string;
  currentBookId: string | null;
  activeTab: string;
  isAdmin: boolean;
  defaultOpen: boolean;
}) {
  const isInThisBook = currentBookId === bookId;
  const isTabActive = (tab: string) => {
    if (!isInThisBook) return false;
    if (tab === "snapshot") return activeTab === "snapshot" || activeTab === "overview";
    return activeTab === tab;
  };

  return (
    <CollapsibleGroup label="BOOK" icon={SECTION_ICON.book} defaultOpen={defaultOpen}>
      {BOOK_TABS.map((tab) => {
        if (tab.adminOnly && !isAdmin) return null;
        if (!isAdmin && !tab.borrowerSafe) return null;
        return (
          <Link
            key={tab.id}
            to={`/books/${bookId}?tab=${tab.id}`}
            className={cn(s.navItem, { [s.active]: isTabActive(tab.id) })}
          >
            <span className={s.navLabel}><span>{tab.label}</span></span>
          </Link>
        );
      })}
    </CollapsibleGroup>
  );
}
